// Talks to the collab Worker's plain HTTP endpoints (`workers/collab/src/worker.ts`):
// reserving a room session, and building the room WebSocket URL from a
// granted token. Kept as plain functions (no state to own) rather than a
// class, per the "classes for state+behaviour, functions for stateless
// helpers" rule.

import {
  COLLAB_AVAILABILITY_PATH,
  COLLAB_JOIN_PATH,
  COLLAB_ROOM_PATH_PREFIX,
  COLLAB_SESSION_PATH,
} from "../app_constants";

import { parseRetainPolicy, type RetainPolicy } from "./roomProtocol";

/**
 * Which of the two distinct Worker transactions a session start is. Opening
 * reserves daily budget for a new room; joining borrows the signed terms of a
 * room somebody else already paid for. Confusing them is what used to limit
 * a room to a single peer, so the caller has to say which one it means.
 */
export type CollabEntryMode = "open" | "join";

/** The known refusal reasons the session and join endpoints can return. */
export type SessionRefusalReason =
  | "disabled"
  | "daily_quota"
  | "ip_quota"
  | "room_not_found"
  | "room_closed"
  | "room_expired"
  | "room_full"
  | "unknown";

export type SessionReservationResult =
  | {
      granted: true;
      roomToken: string;
      expiresAt: number | null;
      /**
       * The policy actually in force, which is not necessarily the one that
       * was asked for: a room that already exists answers with the policy it
       * was opened with. Always act on this value, never on the request's.
       */
      retainPolicy: RetainPolicy;
    }
  | { granted: false; reason: SessionRefusalReason };

/** The reasons `GET /collab/availability` can return (mirrors `AvailabilityDecision` in `workers/collab/src/quota.ts`). */
export type CollabAvailabilityReason =
  | "ok"
  | "daily_quota"
  | "ip_quota"
  | "disabled";

/** The room limits `GET /collab/availability` reports, shared by every session. */
export interface CollabLimits {
  maxPeers: number;
  sessionMaxMinutes: number;
  cursorHz: number;
}

export interface CollabAvailabilityResult {
  allowed: boolean;
  reason: CollabAvailabilityReason;
  remainingSessions: number;
  resetAt: string;
  limits: CollabLimits;
  /** The response's `Cache-Control: max-age=N`, in milliseconds. `null` when absent or unparseable. */
  maxAgeMs: number | null;
}

/**
 * Reads the collab Worker's base URL (e.g. `https://collab.drawola.workers.dev`
 * or `http://localhost:8787` for `wrangler dev`). Declared in
 * `excalidraw-app/vite-env.d.ts` as `VITE_APP_COLLAB_WORKER_URL`; falls back
 * to the local `wrangler dev` default so a fresh checkout can still run the
 * collab path locally without setting up `.env` first.
 */
export const getCollabWorkerBaseUrl = (): string =>
  import.meta.env.VITE_APP_COLLAB_WORKER_URL?.trim() || "http://localhost:8787";

/** Turns an http(s) Worker base URL into its ws(s) equivalent. */
const toWebSocketOrigin = (httpUrl: string): string =>
  httpUrl.replace(/^http/, "ws");

/** Builds the room's WebSocket upgrade URL from a granted room token. */
export const buildRoomWebSocketUrl = (
  workerBaseUrl: string,
  roomId: string,
  roomToken: string,
): string =>
  `${toWebSocketOrigin(
    workerBaseUrl,
  )}${COLLAB_ROOM_PATH_PREFIX}/${encodeURIComponent(
    roomId,
  )}?token=${encodeURIComponent(roomToken)}`;

const KNOWN_SESSION_REFUSAL_REASONS = new Set<string>([
  "disabled",
  "daily_quota",
  "ip_quota",
  "room_not_found",
  "room_closed",
  "room_expired",
  "room_full",
]);

const isSessionRefusalReason = (
  value: unknown,
): value is Exclude<SessionRefusalReason, "unknown"> =>
  typeof value === "string" && KNOWN_SESSION_REFUSAL_REASONS.has(value);

/** Parses the granted response's ISO `expiresAt` into epoch milliseconds, or `null` if absent/invalid. */
const parseExpiresAt = (value: unknown): number | null => {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * Reserves a NEW room session, which is what actually spends daily request
 * budget. Only the peer that opens the room may call this: see
 * `joinRoomSession` for every other peer.
 *
 * `retainPolicy` is the one term the opener gets to decide, and only here.
 * Omitting it means `"discard"`. The room keeps whatever it was opened with,
 * so a second call naming a different policy changes nothing and is answered
 * with the room's own value.
 */
export const reserveRoomSession = (
  workerBaseUrl: string,
  roomId: string,
  retainPolicy?: RetainPolicy,
): Promise<SessionReservationResult> =>
  requestRoomSession(
    `${workerBaseUrl}${COLLAB_SESSION_PATH}`,
    roomId,
    retainPolicy,
  );

/**
 * Obtains a capability for a room somebody else already opened. The Worker
 * hands back that room's own signed terms rather than creating a second
 * reservation, so the relay accepts this peer into the same session instead
 * of refusing it as a session mismatch.
 *
 * No `retainPolicy` is sent: a joiner does not get to choose one, and the
 * endpoint would ignore it anyway.
 */
export const joinRoomSession = (
  workerBaseUrl: string,
  roomId: string,
): Promise<SessionReservationResult> =>
  requestRoomSession(`${workerBaseUrl}${COLLAB_JOIN_PATH}`, roomId);

/**
 * Posts one room request and normalizes both endpoints' identical response
 * shape. Never throws: a network failure or an unexpected body is reported as
 * `{ granted: false, reason: "unknown" }` so callers can always show the user
 * something instead of crashing the collaboration flow.
 */
const requestRoomSession = async (
  url: string,
  roomId: string,
  retainPolicy?: RetainPolicy,
): Promise<SessionReservationResult> => {
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // The key is omitted rather than sent as `undefined`: the Worker rejects
      // an unknown key, and an absent one is what means "discard".
      body: JSON.stringify(
        retainPolicy === undefined ? { roomId } : { roomId, retainPolicy },
      ),
    });
    const data: unknown = await response.json();
    if (
      typeof data !== "object" ||
      data === null ||
      typeof (data as { granted?: unknown }).granted !== "boolean"
    ) {
      return { granted: false, reason: "unknown" };
    }
    const {
      granted,
      roomToken,
      reason,
      expiresAt,
      retainPolicy: grantedRetainPolicy,
    } = data as {
      granted: boolean;
      roomToken?: unknown;
      reason?: unknown;
      expiresAt?: unknown;
      retainPolicy?: unknown;
    };
    if (granted && typeof roomToken === "string" && roomToken.length > 0) {
      return {
        granted: true,
        roomToken,
        expiresAt: parseExpiresAt(expiresAt),
        // A Worker older than this client sends no policy, and the room
        // default is discard, so an absent field degrades to the safer answer
        // rather than to a refusal.
        retainPolicy: parseRetainPolicy(grantedRetainPolicy),
      };
    }
    return {
      granted: false,
      reason: isSessionRefusalReason(reason) ? reason : "unknown",
    };
  } catch (error) {
    console.error(`Collaboration room request to ${url} failed`, error);
    return { granted: false, reason: "unknown" };
  }
};

const isCollabAvailabilityReason = (
  value: unknown,
): value is CollabAvailabilityReason =>
  value === "ok" ||
  value === "daily_quota" ||
  value === "ip_quota" ||
  value === "disabled";

const isCollabLimits = (value: unknown): value is CollabLimits =>
  typeof value === "object" &&
  value !== null &&
  typeof (value as CollabLimits).maxPeers === "number" &&
  typeof (value as CollabLimits).sessionMaxMinutes === "number" &&
  typeof (value as CollabLimits).cursorHz === "number";

/** Validates one `GET /collab/availability` JSON body against the documented shape. */
const parseAvailabilityBody = (
  value: unknown,
): Omit<CollabAvailabilityResult, "maxAgeMs"> | null => {
  if (typeof value !== "object" || value === null) {
    return null;
  }
  const body = value as Record<string, unknown>;
  if (
    typeof body.allowed !== "boolean" ||
    !isCollabAvailabilityReason(body.reason) ||
    typeof body.remainingSessions !== "number" ||
    typeof body.resetAt !== "string" ||
    !isCollabLimits(body.limits)
  ) {
    return null;
  }
  return {
    allowed: body.allowed,
    reason: body.reason,
    remainingSessions: body.remainingSessions,
    resetAt: body.resetAt,
    limits: body.limits,
  };
};

/** Parses the `max-age` directive off a `Cache-Control` header, in milliseconds. */
const parseMaxAgeMs = (header: string | null): number | null => {
  const match = header?.match(/max-age=(\d+)/);
  return match ? Number(match[1]) * 1000 : null;
};

/**
 * Fetches `GET /collab/availability`. Never throws: an unreachable Worker or
 * a malformed body reports `null`, so a caller can fail closed (treat
 * collaboration as unavailable) instead of assuming it is allowed. See
 * `useCollabAvailability` for the caching policy built on top of this.
 */
export const fetchCollabAvailability = async (
  workerBaseUrl: string,
): Promise<CollabAvailabilityResult | null> => {
  try {
    const response = await fetch(`${workerBaseUrl}${COLLAB_AVAILABILITY_PATH}`);
    const data: unknown = await response.json();
    const parsed = parseAvailabilityBody(data);
    if (!parsed) {
      return null;
    }
    return {
      ...parsed,
      maxAgeMs: parseMaxAgeMs(response.headers.get("Cache-Control")),
    };
  } catch (error) {
    console.error("Failed to check collaboration availability", error);
    return null;
  }
};
